import queryString from 'query-string';
import { matchPath } from 'react-router-dom';

import { QUERY_PARAMS, ROUTES } from '../../app-core';
import { joinRouteMatchPath } from '../../path';
import { getForecastingByIdRouteUrl, getForecastingRouteUrl } from '../../predict';
import { getProductRoute } from '../../organisations';

export const getOnboardingStartUrl = (): string => {
  return [ROUTES.ONBOARDING_ROUTE, ROUTES.START].join('');
};

export const getOnboardingStartUrlWithParams = (
  workspaceId: string,
  organisationId: string,
  datasourceId: string,
  options: {
    businessId?: string;
    status?: 'success' | 'failure';
    connectedOrgs?: number;
    usedIntegration?: string;
  } = {}
): string => {
  const queryParams = queryString.stringify(
    {
      [QUERY_PARAMS.WORKSPACE_ID]: workspaceId,
      [QUERY_PARAMS.ORG_ID]: organisationId,
      [QUERY_PARAMS.DATASOURCE_ID]: datasourceId,
      [QUERY_PARAMS.STATUS]: options.status || 'success',
      [QUERY_PARAMS.CONNECTED_ORGS]: (options.connectedOrgs ?? '').toString(),
      [QUERY_PARAMS.INTEGRATION]: options.usedIntegration || '',
      [QUERY_PARAMS.BUSINESS_ID]: options.businessId || ''
    },
    { skipEmptyString: true }
  );
  return [getOnboardingStartUrl(), `?${queryParams}`].join('');
};

export const getOnboardingInitialSyncUrl = (
  workspaceId: string,
  organisationId: string,
  datasourceId: string,
  onboardingType: string,
  usedIntegration?: string
) => {
  const queryParams = queryString.stringify({
    [QUERY_PARAMS.WORKSPACE_ID]: workspaceId,
    [QUERY_PARAMS.ORG_ID]: organisationId,
    [QUERY_PARAMS.DATASOURCE_ID]: datasourceId,
    [QUERY_PARAMS.TYPE]: onboardingType,
    ...(usedIntegration ? { [QUERY_PARAMS.INTEGRATION]: usedIntegration } : {})
  });
  return [ROUTES.ONBOARDING_ROUTE, ROUTES.INITIAL_SYNC, `?${queryParams}`].join('');
};

export const getOnboardingSageOrgsUrl = (workspaceId: string, onboardingType: string) => {
  const queryParams = queryString.stringify({
    [QUERY_PARAMS.WORKSPACE_ID]: workspaceId,
    [QUERY_PARAMS.TYPE]: onboardingType
  });
  return [ROUTES.ONBOARDING_ROUTE, ROUTES.SAGE_ORGS, `?${queryParams}`].join('');
};

export const getInitialBudgetDateSelectUrl = (workspaceId: string, organisationId: string) => {
  return `${ROUTES.ONBOARDING_ROUTE}${ROUTES.ONBOARDING_INITIAL}${ROUTES.BUDGET_DATE_SELECT}?${QUERY_PARAMS.WORKSPACE_ID}=${workspaceId}&${QUERY_PARAMS.ORG_ID}=${organisationId}`;
};

export const getForecastOnboardingUrl = (
  workspaceId: string,
  organisationId: string,
  isBudget = true,
  includeInvoices = false
) => {
  const onboardingForecastRoute = isBudget
    ? ROUTES.BUDGET
    : includeInvoices
    ? ROUTES.SCENARIO_WITH_INVOICES
    : ROUTES.SCENARIO;
  return `${getForecastingRouteUrl(workspaceId, organisationId)}${ROUTES.ONBOARDING_ROUTE}${onboardingForecastRoute}`;
};

export const getForecastByIdOnboardingUrl = (
  workspaceId: string,
  organisationId: string,
  forecastId: string,
  isBudget = true,
  includeInvoices = false
) => {
  const onboardingForecastRoute = isBudget
    ? ROUTES.BUDGET
    : includeInvoices
    ? ROUTES.SCENARIO_WITH_INVOICES
    : ROUTES.SCENARIO;
  return `${getForecastingByIdRouteUrl(workspaceId, organisationId, forecastId)}${
    ROUTES.ONBOARDING_ROUTE
  }${onboardingForecastRoute}`;
};

export const getSfaOnboardingUrl = (bid: string) => {
  return joinRouteMatchPath('', ROUTES.ONBOARDING_ROUTE, `${ROUTES.SFA}?bid=${bid}`);
};

export const matchAnyOnboardingRoute = (pathname: string): boolean => pathname.indexOf(ROUTES.ONBOARDING_ROUTE) !== -1;

export const matchOnboardingRoute = (pathname: string): ReturnType<typeof matchPath> => {
  const productPath = getProductRoute();
  const pathForecast = joinRouteMatchPath('', productPath, ROUTES.FORECASTS, ROUTES.ONBOARDING_ROUTE, '*');
  const pathForecastById = joinRouteMatchPath(
    '',
    productPath,
    ROUTES.FORECASTS,
    ROUTES.FORECAST_BY_ID,
    ROUTES.ONBOARDING_ROUTE,
    '*'
  );
  return matchPath({ path: pathForecast }, pathname) || matchPath({ path: pathForecastById }, pathname);
};

export const matchOnboardingForecastByIdRoute = (pathname: string): ReturnType<typeof matchPath> => {
  const productPath = getProductRoute();
  const path = joinRouteMatchPath(
    '',
    productPath,
    ROUTES.FORECASTS,
    ROUTES.FORECAST_BY_ID,
    ROUTES.ONBOARDING_ROUTE,
    '*'
  );
  return matchPath({ path }, pathname);
};

export const matchOnboardingBudgetRoute = (pathname: string): ReturnType<typeof matchPath> => {
  const productPath = getProductRoute();
  const pathForecast = joinRouteMatchPath(
    '',
    productPath,
    ROUTES.FORECASTS,
    ROUTES.ONBOARDING_ROUTE,
    ROUTES.BUDGET,
    '*'
  );
  const pathForecastById = joinRouteMatchPath(
    '',
    productPath,
    ROUTES.FORECASTS,
    ROUTES.FORECAST_BY_ID,
    ROUTES.ONBOARDING_ROUTE,
    ROUTES.BUDGET,
    '*'
  );
  return matchPath({ path: pathForecast }, pathname) || matchPath({ path: pathForecastById }, pathname);
};

export const matchOnboardingScenarioRoute = (pathname: string): ReturnType<typeof matchPath> => {
  const productPath = getProductRoute();
  const pathForecast = joinRouteMatchPath(
    '',
    productPath,
    ROUTES.FORECASTS,
    ROUTES.ONBOARDING_ROUTE,
    ROUTES.SCENARIO,
    '*'
  );
  const pathForecastById = joinRouteMatchPath(
    '',
    productPath,
    ROUTES.FORECASTS,
    ROUTES.FORECAST_BY_ID,
    ROUTES.ONBOARDING_ROUTE,
    ROUTES.SCENARIO,
    '*'
  );
  return matchPath({ path: pathForecast }, pathname) || matchPath({ path: pathForecastById }, pathname);
};

export const matchOnboardingScenarioWithInvoicesRoute = (pathname: string): ReturnType<typeof matchPath> => {
  const productPath = getProductRoute();
  const pathForecast = joinRouteMatchPath(
    '',
    productPath,
    ROUTES.FORECASTS,
    ROUTES.ONBOARDING_ROUTE,
    ROUTES.SCENARIO_WITH_INVOICES,
    '*'
  );
  const pathForecastById = joinRouteMatchPath(
    '',
    productPath,
    ROUTES.FORECASTS,
    ROUTES.FORECAST_BY_ID,
    ROUTES.ONBOARDING_ROUTE,
    ROUTES.SCENARIO_WITH_INVOICES,
    '*'
  );
  return matchPath({ path: pathForecast }, pathname) || matchPath({ path: pathForecastById }, pathname);
};

export const matchOnboardingSFARoute = (pathname: string): ReturnType<typeof matchPath> => {
  return matchPath({ path: joinRouteMatchPath('', ROUTES.ONBOARDING_ROUTE, ROUTES.SFA, '*') }, pathname);
};

export const getOnboardingInitialRoute = (workspaceId: string, organisationId: string, integration?: string) =>
  `${ROUTES.ONBOARDING_ROUTE}${ROUTES.ONBOARDING_INITIAL}${ROUTES.FORECAST_START}?${
    QUERY_PARAMS.WORKSPACE_ID
  }=${workspaceId}&${QUERY_PARAMS.ORG_ID}=${organisationId}${
    integration ? `&${QUERY_PARAMS.INTEGRATION}=${integration}` : ''
  }`;
